import { useEffect, useState } from "react";
import { useGame, MODES } from "./store";

const fmt = (n, d = 1) => (isNaN(n) ? "—" : Number(n).toFixed(d));

/** Toast shown after clearing / crashing a hazard */
function HazardToast() {
  const last = useGame((s) => s.lastHazardResult);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!last) return;
    setVisible(true);
    const id = setTimeout(() => setVisible(false), 5000);
    return () => clearTimeout(id);
  }, [last?.t]);
  if (!last || !visible) return null;
  const color =
    last.outcome === "perfect" ? "#00ffaa"
    : last.outcome === "crashed" ? "#ff4d6d"
    : last.outcome === "cautious" ? "#ffd23f"
    : "#3eb6ff";
  return (
    <div
      data-testid="hazard-toast"
      style={{
        position: "absolute",
        top: "18%",
        left: "50%",
        transform: "translateX(-50%)",
        padding: "14px 26px",
        background: "rgba(10,12,24,0.92)",
        border: `2px solid ${color}`,
        borderRadius: 14,
        color,
        fontFamily: "'Inter', system-ui, sans-serif",
        fontSize: 17,
        fontWeight: 800,
        zIndex: 12,
        boxShadow: `0 0 30px ${color}44`,
        backdropFilter: "blur(8px)",
        maxWidth: "min(620px, 90vw)",
        textAlign: "center",
      }}
    >
      {last.msg}
    </div>
  );
}

/** Bottom-center: the GRIP METER — the heart of drive-by-physics */
function GripMeter() {
  const hud = useGame((s) => s.hazardHUD);
  const mode = useGame((s) => s.mode);
  if (mode !== MODES.ON_TRACK) return null;

  if (!hud) {
    return (
      <div
        style={{
          position: "absolute",
          bottom: 24,
          left: "50%",
          transform: "translateX(-50%)",
          background: "rgba(10, 12, 24, 0.78)",
          color: "#9aa4b2",
          padding: "10px 18px",
          borderRadius: 12,
          fontFamily: "'Inter', system-ui, sans-serif",
          fontSize: 13,
          border: "1px solid rgba(255,255,255,0.1)",
          backdropFilter: "blur(8px)",
          textAlign: "center",
        }}
      >
        ✅ Straight ahead — open it up! &nbsp;·&nbsp;
        <b style={{ color: "#ffd23f" }}>↑ accelerate · ↓ brake</b>
      </div>
    );
  }

  const grip = Math.min(1.25, hud.grip || 0);
  const pct = Math.min(100, grip * 100);
  const barColor = grip < 0.7 ? "#00ffaa" : grip < 0.92 ? "#ffd23f" : "#ff4d6d";
  const danger = grip >= 0.92;

  return (
    <div
      data-testid="grip-meter"
      style={{
        position: "absolute",
        bottom: 24,
        left: "50%",
        transform: "translateX(-50%)",
        width: "min(560px, 92vw)",
        background: "rgba(10, 12, 24, 0.88)",
        color: "#fff",
        padding: "14px 20px",
        borderRadius: 16,
        fontFamily: "'Inter', system-ui, sans-serif",
        border: `2px solid ${danger ? "#ff4d6d" : "rgba(255,255,255,0.12)"}`,
        backdropFilter: "blur(8px)",
        boxShadow: danger ? "0 0 30px rgba(255,77,109,0.4)" : "0 8px 32px rgba(0,0,0,0.4)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: hud.inside ? "#ff8a3d" : "#3eb6ff", letterSpacing: 1 }}>
          {hud.inside ? `🌀 IN ${hud.label.toUpperCase()}` : `⚠ ${hud.label} — ${hud.distM.toFixed(0)} m ahead`}
        </span>
        <span style={{ fontSize: 12, color: "#9aa4b2", fontFamily: "'JetBrains Mono', monospace" }}>
          r = {hud.rMin.toFixed(0)} m
        </span>
      </div>

      {/* Speed vs v_max */}
      <div style={{ display: "flex", justifyContent: "space-between", fontFamily: "'JetBrains Mono', monospace", fontSize: 13, marginBottom: 6 }}>
        <span>
          v = <b style={{ color: barColor }}>{hud.v.toFixed(1)}</b> m/s
        </span>
        <span>
          {hud.vmax != null ? (
            <>v_max = <b style={{ color: "#00ffaa" }}>{hud.vmax.toFixed(1)}</b> m/s</>
          ) : (
            <span style={{ color: "#ffd23f" }}>v_max = ? — solve at the checkpoint</span>
          )}
        </span>
      </div>

      {/* GRIP BAR: (v² / r) needed vs (g·cosθ) available */}
      <div style={{ position: "relative", height: 16, background: "rgba(255,255,255,0.08)", borderRadius: 8, overflow: "hidden" }}>
        <div
          style={{
            position: "absolute",
            inset: 0,
            width: `${pct}%`,
            background: `linear-gradient(90deg, #00ffaa, ${barColor})`,
            borderRadius: 8,
            transition: "width 0.1s linear",
          }}
        />
        {/* The 100% physical limit line */}
        <div style={{ position: "absolute", left: "calc(100% - 2px)", top: 0, bottom: 0, width: 2, background: "#ff4d6d" }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10.5, color: "#9aa4b2", marginTop: 4 }}>
        <span>GRIP NEEDED: m·v²/r</span>
        <span style={{ color: danger ? "#ff4d6d" : "#9aa4b2", fontWeight: danger ? 800 : 400 }}>
          {danger ? "⚠ OVER THE LIMIT — BRAKE (↓) NOW!" : `${pct.toFixed(0)}% of available force`}
        </span>
      </div>
    </div>
  );
}

export default function HUD() {
  const v = useGame((s) => s.v);
  const a = useGame((s) => s.a);
  const N = useGame((s) => s.N);
  const mode = useGame((s) => s.mode);
  const coins = useGame((s) => s.coins);
  const points = useGame((s) => s.points);
  const stationsCrossed = useGame((s) => s.stationsCrossed);
  const totalStations = useGame((s) => s.totalStations);
  const parachuteOpen = useGame((s) => s.parachuteOpen);
  const parachuteArea = useGame((s) => s.parachuteArea);
  const eqs = useGame((s) => s.activeEquations);
  const mass = useGame((s) => s.mass);
  const height = useGame((s) => s.height);
  const nearestStation = useGame((s) => s.nearestStation);
  const currentZone = useGame((s) => s.currentZone);
  const stopwatchActive = useGame((s) => s.stopwatchActive);
  const stopwatchTime = useGame((s) => s.stopwatchTime);

  const modeColor = {
    ON_TRACK: "#00ffaa",
    AIRBORNE: "#ffd23f",
    IN_WATER: "#3eb6ff",
    ON_GROUND: "#ff8a3d",
    RECAP: "#e879f9",
    FINISHED: "#22c55e",
    MENU: "#888",
  }[mode] || "#888";

  return (
    <>
      {/* CLIMB PROMPT (center-top) — shown whenever off the track */}
      {(mode === "ON_GROUND" || mode === "IN_WATER") && (
        <div
          data-testid="climb-prompt"
          style={{
            position: "absolute",
            top: "40%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            padding: "18px 30px",
            background: "rgba(0, 255, 170, 0.18)",
            border: "2px solid #00ffaa",
            borderRadius: 16,
            color: "#00ffaa",
            fontFamily: "'Inter', system-ui, sans-serif",
            fontSize: 22,
            fontWeight: 800,
            letterSpacing: 1,
            backdropFilter: "blur(8px)",
            textShadow: "0 0 20px rgba(0,255,170,0.6)",
            animation: "pulse 1.2s infinite",
            zIndex: 5,
          }}
        >
          🪜 Press <b style={{ color: "#fff" }}>C</b> to climb back onto the track
          {mode === "ON_GROUND" && nearestStation ? ` — ${nearestStation.label}` : ""}
        </div>
      )}
      <style>{`@keyframes pulse {0%,100%{transform:translate(-50%,-50%) scale(1);}50%{transform:translate(-50%,-50%) scale(1.04);}}`}</style>

      {/* TOP-LEFT: Live physics readout */}
      <div
        data-testid="hud-physics-readout"
        style={{
          position: "absolute",
          top: 16,
          left: 16,
          background: "rgba(10, 12, 24, 0.78)",
          color: "#fff",
          padding: "14px 18px",
          borderRadius: 14,
          fontFamily: "'JetBrains Mono', 'Courier New', monospace",
          fontSize: 13,
          minWidth: 230,
          border: "1px solid rgba(255,255,255,0.1)",
          backdropFilter: "blur(8px)",
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: modeColor,
            letterSpacing: 2,
            fontWeight: 700,
            marginBottom: 8,
          }}
        >
          ● MODE: {mode}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", rowGap: 4, columnGap: 12 }}>
          <span style={{ color: "#9aa4b2" }}>speed v</span>
          <span style={{ color: "#ffd23f", textAlign: "right" }}>
            {fmt(v)} m/s
          </span>
          <span style={{ color: "#9aa4b2" }}>accel a</span>
          <span style={{ color: "#ffd23f", textAlign: "right" }}>
            {fmt(a, 2)} m/s²
          </span>
          <span style={{ color: "#9aa4b2" }}>normal N</span>
          <span
            style={{
              color: N > 0 ? "#00ffaa" : "#ff4d6d",
              textAlign: "right",
            }}
          >
            {fmt(N, 0)} N
          </span>
          <span style={{ color: "#9aa4b2" }}>mass m</span>
          <span style={{ textAlign: "right" }}>{mass} kg</span>
          <span style={{ color: "#9aa4b2" }}>height h</span>
          <span style={{ textAlign: "right" }}>{fmt(height)} m</span>
        </div>
      </div>

      {/* TOP-CENTER: Score/coins (moved to make room for Physics Console on right) */}
      <div
        data-testid="hud-score"
        style={{
          position: "absolute",
          top: 16,
          left: "50%",
          transform: "translateX(-50%)",
          background: "rgba(10, 12, 24, 0.78)",
          color: "#fff",
          padding: "10px 20px",
          borderRadius: 14,
          fontFamily: "'Inter', system-ui, sans-serif",
          fontSize: 14,
          border: "1px solid rgba(255,255,255,0.1)",
          backdropFilter: "blur(8px)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 22 }}>
          <div style={{ fontSize: 22, color: "#ffd23f", fontWeight: 700 }}>
            🪙 {coins}
          </div>
          <div style={{ fontSize: 16, color: "#00ffaa" }}>
            ★ {points} pts
          </div>
          <div style={{ fontSize: 13, color: "#9aa4b2" }}>
            {stationsCrossed}/{totalStations} stations
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 15, background: "rgba(255,255,255,0.05)", padding: "4px 12px", borderRadius: 8 }}>
          <span style={{ color: "#e879f9", fontWeight: 600 }}>{currentZone.replace("_", " ")}</span>
          {stopwatchActive && (
            <span style={{ color: "#ffd23f", fontFamily: "monospace" }}>
              ⏱ {stopwatchTime.toFixed(1)}s
            </span>
          )}
        </div>
      </div>

      {/* BOTTOM-LEFT: Active equations */}
      <div
        data-testid="hud-equations"
        style={{
          position: "absolute",
          bottom: 16,
          left: 16,
          background: "rgba(10, 12, 24, 0.78)",
          color: "#fff",
          padding: "14px 18px",
          borderRadius: 14,
          fontFamily: "'JetBrains Mono', 'Courier New', monospace",
          fontSize: 12.5,
          maxWidth: 460,
          border: "1px solid rgba(255,255,255,0.1)",
          backdropFilter: "blur(8px)",
        }}
      >
        <div
          style={{
            fontSize: 10,
            letterSpacing: 2,
            color: "#e879f9",
            marginBottom: 8,
            fontWeight: 700,
          }}
        >
          ⚛ LIVE PHYSICS
        </div>
        {(eqs || []).map((e, i) => (
          <div key={i} style={{ marginBottom: 4, lineHeight: 1.5 }}>
            {e}
          </div>
        ))}
      </div>

      {/* BOTTOM-CENTER: Grip meter — drive-by-physics core display */}
      <GripMeter />

      {/* Hazard result toast */}
      <HazardToast />

      {/* CHALLENGE METRICS STRIP */}
      <ChallengeStrip />
    </>
  );
}

/** Live optimization metrics while a challenge runs */
function ChallengeStrip() {
  const goal = useGame((s) => s.challengeGoal);
  const metrics = useGame((s) => s.challengeMetrics);
  const globalTimer = useGame((s) => s.globalTimer);
  const challengeMu = useGame((s) => s.challengeMu);
  if (!goal) return null;
  const hl = (g) => (goal === g ? { color: "#ffd23f", fontWeight: 800 } : { color: "#9aa4b2" });
  return (
    <div
      data-testid="challenge-strip"
      style={{
        position: "absolute",
        top: 86,
        left: "50%",
        transform: "translateX(-50%)",
        background: "rgba(10,12,24,0.85)",
        border: "1px solid rgba(255,210,63,0.4)",
        borderRadius: 12,
        padding: "8px 16px",
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 12.5,
        color: "#fff",
        display: "flex",
        gap: 16,
        backdropFilter: "blur(8px)",
        zIndex: 6,
      }}
    >
      <span style={{ color: "#ff8a3d", fontWeight: 800 }}>🏁 μ={challengeMu.toFixed(2)}</span>
      <span style={hl("energy")}>⚡ {(metrics.energy / 1000).toFixed(1)} kJ</span>
      <span style={hl("wear")}>🛞 {(metrics.wear / 1000).toFixed(1)} kJ</span>
      <span style={hl("risk")}>⚠ {metrics.riskTime.toFixed(1)} s</span>
      <span style={hl("time")}>⏱ {globalTimer.toFixed(1)} s</span>
    </div>
  );
}
